import { normalizeStructTag, normalizeSuiAddress } from '@mysten/sui/utils';

import {
  type ParsedCallArg,
  type ParsedCommand,
  SendAssertionError,
  type TransferValidationContext,
} from './transaction-validation';

function assertWithdrawalNetwork(
  expiration: TransferValidationContext['expiration'],
  chainIdentifier: string | undefined,
): void {
  // Withdrawals have no sender-owned object reference to bind approval to the selected network.
  if (
    !chainIdentifier ||
    expiration.$kind !== 'ValidDuring' ||
    expiration.ValidDuring.chain !== chainIdentifier
  ) {
    throw new SendAssertionError('the address withdrawal is not bound to your Sui network');
  }
}

export function readApprovedAddressWithdrawal({
  commands,
  inputs,
  expected,
  expiration,
}: TransferValidationContext): bigint {
  assertWithdrawalNetwork(expiration, expected.chainIdentifier);
  const redemption = commands[0];
  const withdrawals = inputs.filter((input) => input.$kind === 'FundsWithdrawal');
  if (withdrawals.length !== 1 || redemption?.$kind !== 'MoveCall') {
    throw new SendAssertionError('it must redeem exactly one address withdrawal');
  }
  const [withdrawalInput] = withdrawals;
  assertRedeemCall(redemption.MoveCall, withdrawalInput, inputs, expected.coinType);

  const withdrawal = withdrawalInput.FundsWithdrawal;
  const amount = BigInt(withdrawal.reservation.MaxAmountU64);
  if (
    withdrawal.withdrawFrom.$kind !== 'Sender' ||
    normalizeStructTag(withdrawal.typeArg.Balance) !== normalizeStructTag(expected.coinType) ||
    amount <= 0n ||
    amount > expected.amount
  ) {
    throw new SendAssertionError(
      'the address withdrawal does not match your approved coin and amount',
    );
  }
  return amount;
}

function assertRedeemCall(
  call: Extract<ParsedCommand, { $kind: 'MoveCall' }>['MoveCall'],
  withdrawalInput: ParsedCallArg,
  inputs: ParsedCallArg[],
  coinType: string,
): void {
  const isCoinRedemption =
    normalizeSuiAddress(call.package) === normalizeSuiAddress('0x2') &&
    call.module === 'coin' &&
    call.function === 'redeem_funds';
  const usesApprovedCoinType =
    call.typeArguments.length === 1 &&
    normalizeStructTag(call.typeArguments[0]) === normalizeStructTag(coinType);
  const [argument] = call.arguments;
  const usesWithdrawalInput =
    call.arguments.length === 1 &&
    argument?.$kind === 'Input' &&
    inputs[argument.Input] === withdrawalInput;

  if (!isCoinRedemption || !usesApprovedCoinType || !usesWithdrawalInput) {
    throw new SendAssertionError(
      'the address withdrawal does not match your approved coin and amount',
    );
  }
}
